import { useEffect, useMemo, useState } from 'react'
import AdminShell from './AdminShell'
import {
  AdminButton,
  AdminCard,
  AdminField,
  AdminInput,
  AdminModal,
  AdminSkeleton,
  AdminStatePanel,
  AdminStatusPill,
  AdminToastRegion,
  type AdminToast,
} from './AdminUi'
import {
  attachCheckoutAddressAutocomplete,
  clearSelectedCheckoutPlace,
} from '../checkout/addressAutocomplete'
import '../styles/admin-pricing-delivery.css'

type SessionPayload = {
  authenticated: true
  user: { email: string; role: string }
  csrfToken: string
}

type EditableSettings = {
  pack4Cents: number
  pack6Cents: number
  delivery0To8Cents: number
  delivery8To15Cents: number
  delivery15To35Cents: number
}

type PricingSettings = {
  currency: 'NZD'
  version: number
  updatedAt: string
  fixed: {
    packSizes: [4, 6]
    distanceThresholdsKm: [8, 15, 35]
    maxDistanceKm: 35
    methodology: 'Haversine'
    namedGeographicExclusions: false
  }
  editable: EditableSettings
}

type SettingsResponse = { settings: PricingSettings }

type DraftValues = {
  pack4: string
  pack6: string
  delivery0To8: string
  delivery8To15: string
  delivery15To35: string
}

type Change = {
  key: keyof EditableSettings
  label: string
  beforeCents: number
  afterCents: number
}

type DeliveryTestResult = {
  eligible: boolean
  distanceKm: number
  maxDistanceKm: 35
  band: { minExclusiveKm: number | null; maxDistanceKm: number } | null
  fee: number | null
  currency: 'NZD'
}

const MONEY = new Intl.NumberFormat('en-NZ', {
  style: 'currency',
  currency: 'NZD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const DATE_TIME = new Intl.DateTimeFormat('en-NZ', {
  timeZone: 'Pacific/Auckland',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

const FIELD_DEFINITIONS: Array<{
  key: keyof EditableSettings
  draftKey: keyof DraftValues
  label: string
}> = [
  { key: 'pack4Cents', draftKey: 'pack4', label: '4-Pack' },
  { key: 'pack6Cents', draftKey: 'pack6', label: '6-Pack' },
  { key: 'delivery0To8Cents', draftKey: 'delivery0To8', label: '0–8 km delivery' },
  { key: 'delivery8To15Cents', draftKey: 'delivery8To15', label: '>8–15 km delivery' },
  { key: 'delivery15To35Cents', draftKey: 'delivery15To35', label: '>15–35 km delivery' },
]

function environmentFromHostname(hostname: string) {
  const host = hostname.toLowerCase()
  if (host.includes('staging')) return 'Staging'
  if (host === 'localhost' || host === '127.0.0.1') return 'Local'
  return undefined
}

function centsToInput(cents: number) {
  return (cents / 100).toFixed(2)
}

function centsToMoney(cents: number) {
  return MONEY.format(cents / 100)
}

function decimalToCents(value: string, allowZero: boolean): number | null {
  const normalized = value.trim()
  const match = /^(\d+)(?:\.(\d{0,2}))?$/.exec(normalized)
  if (!match) return null
  const whole = Number(match[1])
  const decimal = (match[2] ?? '').padEnd(2, '0')
  const cents = whole * 100 + Number(decimal || '0')
  if (!Number.isSafeInteger(cents) || cents > 10_000_000) return null
  if (allowZero ? cents < 0 : cents <= 0) return null
  return cents
}

function draftFromSettings(settings: PricingSettings): DraftValues {
  return {
    pack4: centsToInput(settings.editable.pack4Cents),
    pack6: centsToInput(settings.editable.pack6Cents),
    delivery0To8: centsToInput(settings.editable.delivery0To8Cents),
    delivery8To15: centsToInput(settings.editable.delivery8To15Cents),
    delivery15To35: centsToInput(settings.editable.delivery15To35Cents),
  }
}

function parsedDraft(draft: DraftValues): EditableSettings | null {
  const pack4Cents = decimalToCents(draft.pack4, false)
  const pack6Cents = decimalToCents(draft.pack6, false)
  const delivery0To8Cents = decimalToCents(draft.delivery0To8, true)
  const delivery8To15Cents = decimalToCents(draft.delivery8To15, true)
  const delivery15To35Cents = decimalToCents(draft.delivery15To35, true)
  if (pack4Cents === null || pack6Cents === null
    || delivery0To8Cents === null || delivery8To15Cents === null || delivery15To35Cents === null) return null
  return { pack4Cents, pack6Cents, delivery0To8Cents, delivery8To15Cents, delivery15To35Cents }
}

function changesFrom(settings: PricingSettings, draft: DraftValues): Change[] {
  const parsed = parsedDraft(draft)
  if (!parsed) return []
  return FIELD_DEFINITIONS.flatMap((field) => {
    const beforeCents = settings.editable[field.key]
    const afterCents = parsed[field.key]
    return beforeCents === afterCents ? [] : [{ key: field.key, label: field.label, beforeCents, afterCents }]
  })
}

function bandLabel(result: DeliveryTestResult) {
  if (!result.band) return `Over ${result.maxDistanceKm} km`
  if (result.band.minExclusiveKm === null) return `0–${result.band.maxDistanceKm} km`
  return `>${result.band.minExclusiveKm}–${result.band.maxDistanceKm} km`
}

export default function AdminPricingDeliveryPage() {
  const [session, setSession] = useState<SessionPayload | null>(null)
  const [settings, setSettings] = useState<PricingSettings | null>(null)
  const [draft, setDraft] = useState<DraftValues | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [logoutBusy, setLogoutBusy] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [toasts, setToasts] = useState<AdminToast[]>([])
  const [testAddress, setTestAddress] = useState('')
  const [testSuburb, setTestSuburb] = useState('')
  const [testPostcode, setTestPostcode] = useState('')
  const [testBusy, setTestBusy] = useState(false)
  const [testError, setTestError] = useState('')
  const [testResult, setTestResult] = useState<DeliveryTestResult | null>(null)
  const environmentLabel = useMemo(() => environmentFromHostname(window.location.hostname), [])

  const parsed = draft ? parsedDraft(draft) : null
  const changes = settings && draft ? changesFrom(settings, draft) : []
  const dirty = changes.length > 0
  const valid = parsed !== null

  function pushToast(title: string, message?: string) {
    const id = crypto.randomUUID()
    setToasts([{ id, title, message, tone: 'success' }])
    window.setTimeout(() => setToasts((current) => current.filter((toast) => toast.id !== id)), 4_000)
  }

  async function load() {
    setLoading(true)
    setError('')
    try {
      const [sessionResponse, settingsResponse] = await Promise.all([
        fetch('/api/admin/session', { credentials: 'same-origin', cache: 'no-store' }),
        fetch('/api/admin/pricing-delivery', { credentials: 'same-origin', cache: 'no-store' }),
      ])
      if (sessionResponse.status === 401 || settingsResponse.status === 401) {
        window.location.replace('/admin/login?next=/admin/pricing-delivery')
        return
      }
      if (!sessionResponse.ok || !settingsResponse.ok) throw new Error('Pricing & Delivery could not be loaded.')
      const nextSession = await sessionResponse.json() as SessionPayload
      const payload = await settingsResponse.json() as SettingsResponse
      setSession(nextSession)
      setSettings(payload.settings)
      setDraft(draftFromSettings(payload.settings))
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Pricing & Delivery could not be loaded.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [])

  useEffect(() => {
    if (loading) return undefined

    const addressInput = document.querySelector<HTMLInputElement>('#admin-delivery-test-address')
    const suburbInput = document.querySelector<HTMLInputElement>('#admin-delivery-test-suburb')
    const postcodeInput = document.querySelector<HTMLInputElement>('#admin-delivery-test-postcode')
    if (!addressInput) return undefined

    let active = true
    let detachAutocomplete: () => void = () => undefined
    clearSelectedCheckoutPlace()

    void attachCheckoutAddressAutocomplete({
      addressInput,
      suburbInput,
      postcodeInput,
    }).then((cleanup) => {
      if (!active) {
        cleanup()
        return
      }
      detachAutocomplete = cleanup
    })

    return () => {
      active = false
      detachAutocomplete()
      clearSelectedCheckoutPlace()
    }
  }, [loading])

  async function logout() {
    if (!session || logoutBusy) return
    setLogoutBusy(true)
    try {
      const response = await fetch('/api/admin/logout', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-CSRF-Token': session.csrfToken },
      })
      if (!response.ok) throw new Error('Logout failed.')
      window.location.replace('/admin/login')
    } catch {
      setError('Logout could not be completed.')
      setLogoutBusy(false)
    }
  }

  function updateDraft(key: keyof DraftValues, value: string) {
    setDraft((current) => current ? { ...current, [key]: value } : current)
  }

  async function saveConfirmed() {
    if (!session || !settings || !parsed || saving || !dirty) return
    setConfirmOpen(false)
    setSaving(true)
    setError('')
    try {
      const response = await fetch('/api/admin/pricing-delivery', {
        method: 'PUT',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': session.csrfToken,
        },
        body: JSON.stringify({ ...parsed, expectedVersion: settings.version }),
      })
      const payload = await response.json().catch(() => null) as (SettingsResponse & { message?: string }) | null
      if (response.status === 401) {
        window.location.replace('/admin/login?next=/admin/pricing-delivery')
        return
      }
      if (!response.ok || !payload?.settings) throw new Error(payload?.message || 'Pricing changes could not be saved.')
      setSettings(payload.settings)
      setDraft(draftFromSettings(payload.settings))
      pushToast('Pricing updated', 'The new values are now authoritative for new customer orders and delivery quotes.')
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Pricing changes could not be saved.')
    } finally {
      setSaving(false)
    }
  }

  function resetDraft() {
    if (!settings) return
    setDraft(draftFromSettings(settings))
    setError('')
  }

  async function runDeliveryTest() {
    if (!session || testBusy || !testAddress.trim() || !testSuburb.trim()) return
    setTestBusy(true)
    setTestError('')
    setTestResult(null)
    try {
      const response = await fetch('/api/admin/pricing-delivery/test-delivery', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': session.csrfToken,
        },
        body: JSON.stringify({ address: testAddress, suburb: testSuburb, postcode: testPostcode }),
      })
      const payload = await response.json().catch(() => null) as (DeliveryTestResult & { message?: string }) | null
      if (response.status === 401) {
        window.location.replace('/admin/login?next=/admin/pricing-delivery')
        return
      }
      if (response.status === 422 && payload && typeof payload.eligible === 'boolean') {
        setTestResult(payload)
        return
      }
      if (!response.ok || !payload || typeof payload.eligible !== 'boolean') {
        throw new Error(payload?.message || 'Delivery could not be tested right now.')
      }
      setTestResult(payload)
    } catch (caught) {
      setTestError(caught instanceof Error ? caught.message : 'Delivery could not be tested right now.')
    } finally {
      setTestBusy(false)
    }
  }

  if (error && (!session || !settings || !draft)) {
    return (
      <main className="admin-shell-gate-error">
        <AdminStatePanel state="error" title="Pricing & Delivery unavailable" description={error} action={<AdminButton onClick={() => void load()}>Retry</AdminButton>} />
      </main>
    )
  }

  if (loading || !session || !settings || !draft) {
    return <main className="admin-shell-gate-loading"><AdminCard><AdminSkeleton lines={9} /></AdminCard></main>
  }

  return (
    <AdminShell
      title="Pricing & Delivery"
      eyebrow="OPERATIONS"
      description="Manage pack prices and delivery fees used for new customer orders."
      user={session.user}
      onLogout={() => void logout()}
      logoutBusy={logoutBusy}
      environmentLabel={environmentLabel}
      actions={<AdminButton variant="secondary" disabled={saving || dirty} onClick={() => void load()}>Refresh</AdminButton>}
    >
      <div className="admin-pricing-delivery-page">
        {error ? <div className="admin-pricing-delivery__error" role="alert">{error}</div> : null}

        <section className="admin-pricing-delivery__summary" aria-label="Pricing authority summary">
          <div><span>4-Pack</span><strong>{centsToMoney(settings.editable.pack4Cents)}</strong><small>4 items · fixed pack size</small></div>
          <div><span>6-Pack</span><strong>{centsToMoney(settings.editable.pack6Cents)}</strong><small>6 items · fixed pack size</small></div>
          <div><span>Delivery from</span><strong>{centsToMoney(settings.editable.delivery0To8Cents)}</strong><small>0–8 km</small></div>
          <div><span>Maximum distance</span><strong>35 km</strong><small>Haversine · fixed boundary</small></div>
        </section>

        <div className="admin-pricing-delivery__grid">
          <div className="admin-pricing-delivery__main">
            <AdminCard className="admin-pricing-delivery__card">
              <div className="admin-pricing-delivery__card-heading">
                <div>
                  <span className="admin-pricing-delivery__eyebrow">BOX PRICING</span>
                  <h2>Pack prices</h2>
                  <p>These prices are shown on Build Your Box and validated again by Checkout.</p>
                </div>
                <AdminStatusPill tone="success">Authoritative</AdminStatusPill>
              </div>

              <div className="admin-pricing-delivery__field-grid">
                <AdminField label="4-Pack price" hint={`Customer label: 4-Pack · ${parsed ? centsToMoney(parsed.pack4Cents) : 'Invalid amount'}`}>
                  <div className="admin-pricing-delivery__money-input"><span>$</span><AdminInput type="text" inputMode="decimal" value={draft.pack4} disabled={saving} onChange={(event) => updateDraft('pack4', event.target.value)} /></div>
                </AdminField>
                <AdminField label="6-Pack price" hint={`Customer label: 6-Pack · ${parsed ? centsToMoney(parsed.pack6Cents) : 'Invalid amount'}`}>
                  <div className="admin-pricing-delivery__money-input"><span>$</span><AdminInput type="text" inputMode="decimal" value={draft.pack6} disabled={saving} onChange={(event) => updateDraft('pack6', event.target.value)} /></div>
                </AdminField>
              </div>
              <p className="admin-pricing-delivery__fixed-note">Pack sizes are fixed at 4 and 6 in V1. Only the prices are editable here.</p>
            </AdminCard>

            <AdminCard className="admin-pricing-delivery__card">
              <div className="admin-pricing-delivery__card-heading">
                <div>
                  <span className="admin-pricing-delivery__eyebrow">DELIVERY FEES</span>
                  <h2>Distance bands</h2>
                  <p>Fees use the existing verified-address Haversine calculation. Distance thresholds are fixed.</p>
                </div>
                <AdminStatusPill tone="info">Max 35 km</AdminStatusPill>
              </div>

              <div className="admin-pricing-delivery__delivery-fields">
                <AdminField label="0–8 km" hint="Includes addresses up to and including 8 km.">
                  <div className="admin-pricing-delivery__money-input"><span>$</span><AdminInput type="text" inputMode="decimal" value={draft.delivery0To8} disabled={saving} onChange={(event) => updateDraft('delivery0To8', event.target.value)} /></div>
                </AdminField>
                <AdminField label=">8–15 km" hint="More than 8 km, up to and including 15 km.">
                  <div className="admin-pricing-delivery__money-input"><span>$</span><AdminInput type="text" inputMode="decimal" value={draft.delivery8To15} disabled={saving} onChange={(event) => updateDraft('delivery8To15', event.target.value)} /></div>
                </AdminField>
                <AdminField label=">15–35 km" hint="More than 15 km, up to and including 35 km.">
                  <div className="admin-pricing-delivery__money-input"><span>$</span><AdminInput type="text" inputMode="decimal" value={draft.delivery15To35} disabled={saving} onChange={(event) => updateDraft('delivery15To35', event.target.value)} /></div>
                </AdminField>
                <div className="admin-pricing-delivery__unavailable-band">
                  <span>&gt;35 km</span>
                  <strong>Unavailable</strong>
                  <small>Fixed V1 maximum. Not editable.</small>
                </div>
              </div>
            </AdminCard>

            <AdminCard className="admin-pricing-delivery__save-card">
              <div>
                <AdminStatusPill tone={dirty ? 'warning' : 'success'}>{dirty ? 'Unsaved changes' : 'Saved'}</AdminStatusPill>
                <strong>{dirty ? `${changes.length} value${changes.length === 1 ? '' : 's'} changed` : 'Pricing matches the saved authority'}</strong>
                <span>{valid ? 'Changes affect new carts, Checkout validation and new delivery quotes after confirmation.' : 'Correct invalid monetary values before saving.'}</span>
              </div>
              <div className="admin-pricing-delivery__save-actions">
                <AdminButton variant="secondary" type="button" disabled={saving || !dirty} onClick={resetDraft}>Discard changes</AdminButton>
                <AdminButton type="button" disabled={saving || !dirty || !valid} onClick={() => setConfirmOpen(true)}>Save changes</AdminButton>
              </div>
            </AdminCard>
          </div>

          <aside className="admin-pricing-delivery__side">
            <AdminCard className="admin-pricing-delivery__ops-card">
              <span className="admin-pricing-delivery__eyebrow">DELIVERY OPERATION</span>
              <h2>Weekend delivery</h2>
              <dl>
                <div><dt>Days</dt><dd>Saturday + Sunday</dd></div>
                <div><dt>Window</dt><dd>3:00 pm–6:00 pm</dd></div>
                <div><dt>Eligibility</dt><dd>Verified address · max 35 km</dd></div>
                <div><dt>Method</dt><dd>Haversine distance</dd></div>
              </dl>
              <a className="admin-pricing-delivery__link" href="/admin/store-status">Open Store Status →</a>
              <p>Store Status controls whether ordering is open. Pricing & Delivery does not duplicate those controls.</p>
            </AdminCard>

            <AdminCard className="admin-pricing-delivery__tester">
              <span className="admin-pricing-delivery__eyebrow">DELIVERY TESTER</span>
              <h2>Check an address</h2>
              <p>Uses the same server-side geocoding and Haversine calculation as customer Checkout.</p>
              <div className="admin-pricing-delivery__tester-fields">
                <AdminField label="Street address" required>
                  <AdminInput id="admin-delivery-test-address" type="text" autoComplete="street-address" value={testAddress} disabled={testBusy} onChange={(event) => setTestAddress(event.target.value)} />
                </AdminField>
                <AdminField label="Suburb / town" required>
                  <AdminInput id="admin-delivery-test-suburb" type="text" autoComplete="address-level2" value={testSuburb} disabled={testBusy} onChange={(event) => setTestSuburb(event.target.value)} />
                </AdminField>
                <AdminField label="Postcode" hint="Optional">
                  <AdminInput id="admin-delivery-test-postcode" type="text" inputMode="numeric" autoComplete="postal-code" value={testPostcode} disabled={testBusy} onChange={(event) => setTestPostcode(event.target.value)} />
                </AdminField>
              </div>
              <AdminButton type="button" disabled={testBusy || !testAddress.trim() || !testSuburb.trim()} onClick={() => void runDeliveryTest()}>{testBusy ? 'Checking…' : 'Check delivery'}</AdminButton>
              {testError ? <div className="admin-pricing-delivery__tester-error" role="alert">{testError}</div> : null}
              {testResult ? (
                <div className={`admin-pricing-delivery__tester-result ${testResult.eligible ? 'is-eligible' : 'is-ineligible'}`} role="status">
                  <AdminStatusPill tone={testResult.eligible ? 'success' : 'danger'}>{testResult.eligible ? 'Eligible' : 'Outside area'}</AdminStatusPill>
                  <dl>
                    <div><dt>Distance</dt><dd>{testResult.distanceKm.toFixed(1)} km</dd></div>
                    <div><dt>Band</dt><dd>{bandLabel(testResult)}</dd></div>
                    <div><dt>Fee</dt><dd>{testResult.fee === null ? 'Unavailable' : MONEY.format(testResult.fee)}</dd></div>
                  </dl>
                </div>
              ) : null}
            </AdminCard>

            <AdminCard className="admin-pricing-delivery__authority-card">
              <span className="admin-pricing-delivery__eyebrow">AUTHORITY</span>
              <h2>Current settings</h2>
              <p>Version {settings.version} · Updated {DATE_TIME.format(new Date(settings.updatedAt))}</p>
              <p>Checkout uses these saved prices for all new orders.</p>
            </AdminCard>
          </aside>
        </div>
      </div>

      <AdminModal
        open={confirmOpen}
        title="Confirm pricing changes"
        description="These values affect new customer carts, Checkout totals and delivery quotes. Review only the values that changed."
        onClose={() => setConfirmOpen(false)}
        footer={(
          <>
            <AdminButton variant="secondary" type="button" onClick={() => setConfirmOpen(false)}>Cancel</AdminButton>
            <AdminButton type="button" disabled={saving} onClick={() => void saveConfirmed()}>Confirm & save</AdminButton>
          </>
        )}
      >
        <div className="admin-pricing-delivery__change-list">
          {changes.map((change) => (
            <div key={change.key}>
              <strong>{change.label}</strong>
              <span>{centsToMoney(change.beforeCents)} <b aria-hidden="true">→</b> {centsToMoney(change.afterCents)}</span>
            </div>
          ))}
        </div>
      </AdminModal>

      <AdminToastRegion toasts={toasts} onDismiss={(id) => setToasts((current) => current.filter((toast) => toast.id !== id))} />
    </AdminShell>
  )
}
